import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { Edition } from "../../../../api/graphql";
import { EditionListStore } from "../../store";
import { editionDocumentationWorkflowStateDisplayId } from "../../../editionDocumentationWorkflowState/store/EditionDocumentationWorkflowStateCommonStore";
import { editionTypeDisplayId } from "../../../editionType/store/EditionTypeCommonStore";
import { editionDocumentationDisplayId } from "../../../editionDocumentation/store/EditionDocumentationCommonStore";
import { associatedDocumentEditionDisplayId } from "../../../associatedDocumentEdition/store/AssociatedDocumentEditionCommonStore";
import { userAttachmentEditionDisplayId } from "../../../userAttachmentEdition/store/UserAttachmentEditionCommonStore";
import { diaDeCalendario, formatearDia } from "../../../../common/calendarDate";
import { MusicalWorkPanel } from "./MusicalWorkPanel";
import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: EditionListStore };

export const EditionListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<Edition>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: Edition, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: Edition, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: Edition) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: Edition, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<Edition>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "documentationState") {
          return {
            ...col,
            id: "documentationState",
            accessorFn: (row: Edition) =>
              row.documentationState
                ? editionDocumentationWorkflowStateDisplayId(
                    row.documentationState,
                    langStore
                  )
                : "-",
          };
        }
        if (col.accessorKey === "publicationDate") {
          return {
            ...col,
            id: "publicationDate",
            accessorFn: (row: Edition) =>
              formatearDia(
                diaDeCalendario(row.publicationDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        if (col.accessorKey === "editionType") {
          return {
            ...col,
            id: "editionType",
            accessorFn: (row: Edition) =>
              row.editionType
                ? editionTypeDisplayId(row.editionType, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "contentType") {
          return {
            ...col,
            id: "contentType",
            accessorFn: (row: Edition) => {
              const v = (row as any).contentType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EditionContentTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "works") {
          return {
            ...col,
            id: "works",
            accessorFn: (row: Edition) =>
              row.works?.length ? `${row.works.length} item(s)` : "-",
          };
        }
        if (col.accessorKey === "documentation") {
          return {
            ...col,
            id: "documentation",
            accessorFn: (row: Edition) =>
              row.documentation?.length
                ? row.documentation
                    .map((x: any) =>
                      editionDocumentationDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "associatedDocuments") {
          return {
            ...col,
            id: "associatedDocuments",
            accessorFn: (row: Edition) =>
              row.associatedDocuments?.length
                ? row.associatedDocuments
                    .map((x: any) =>
                      associatedDocumentEditionDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "userAttachments") {
          return {
            ...col,
            id: "userAttachments",
            accessorFn: (row: Edition) =>
              row.userAttachments?.length
                ? row.userAttachments
                    .map((x: any) =>
                      userAttachmentEditionDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "createdAt_") {
          return {
            ...col,
            id: "createdAt_",
            accessorFn: (row: Edition) =>
              row.createdAt_
                ? new Date(row.createdAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lastUpdatedAt_") {
          return {
            ...col,
            id: "lastUpdatedAt_",
            accessorFn: (row: Edition) =>
              row.lastUpdatedAt_
                ? new Date(row.lastUpdatedAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.editionCurrentPage || [];

    return (
      <Box id={"ListOfEdition"}>
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ pl: 4, pr: 2, pb: 2, backgroundColor: "action.hover" }}>
              <MusicalWorkPanel
                parentId={row.original.id}
                refIds={(row.original.works || []).map((x: any) => x.id)}
              />
            </Box>
          )}
        />
      </Box>
    );
  }
);
